import { useState, useEffect, useCallback, useRef } from 'react';
import { useMeetingSelector } from '../meeting/meetingProviderContextDef';

const NOOP = () => {};
/**
 * Hook to get a Stream by ID and access its methods.
 * @param {string} streamId
 * @param {{
 *  onStreamStateChanged?: Function,
 * }} options
 * @returns {{
 * kind: 'audio' | 'video' | 'share' | 'shareAudio'
 * stream: Stream;
 * codec: string;
 * track: MediaStreamTrack;
 * paused: boolean;
 * pause: () => void,
 * resume: () => void,
 * }}
 */
const useStream = (streamId, { onStreamStateChanged = NOOP } = {}) => {
  const selectStream = useCallback(
    s => {
      if (!streamId || !s.participants) return null;
      for (const p of s.participants.values()) {
        const stream = p.streams.get(streamId);
        if (stream != null) return stream;
      }
      return null;
    },
    [streamId]
  );

  const foundStream = useMeetingSelector(selectStream);

  const [stream, setStream] = useState(null);
  const streamRef = useRef(stream);
  const onStreamStateChangedRef = useRef(onStreamStateChanged);

  streamRef.current = stream;
  onStreamStateChangedRef.current = onStreamStateChanged;

  const _handleStreamStateChanged = useCallback(data => {
    onStreamStateChangedRef.current(data);
  }, []);

  useEffect(() => {
    if (!foundStream) return;

    setStream(foundStream);
    foundStream.on('state', _handleStreamStateChanged);

    return () => {
      foundStream.off('state', _handleStreamStateChanged);
      setStream(null);
    };
  }, [foundStream, _handleStreamStateChanged]);

  const pause = useCallback(() => streamRef.current?.pause(), []);
  const resume = useCallback(() => streamRef.current?.resume(), []);

  return {
    kind: stream?.kind,
    codec: stream?.codec,
    track: stream?.track,
    paused: stream?.paused,
    stream,
    pause,
    resume
  };
};

export default useStream;
